import React, { useEffect, useRef, useState } from 'react'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { setupLights } from './lightSetup'

const AVATAR_PREVIEW_CONFIG = {
  camera: { position: [0, 0, 3] as [number, number, number], orthoViewSize: 3 },
  model: {
    defaultScale: 2.5,
    defaultPosition: [0, -4, 0] as [number, number, number],
  },
  background: {
    position: [0, 0, -5] as [number, number, number],
    color: 0xfafafa,
  },
  renderer: { pixelRatio: 3 },
}

interface AvatarPreviewProps {
  previewUrl: string | null
  backgroundColor?: number
  backgroundImageUrl?: string | null
  scale?: number
  position?: [number, number, number]
  enableControls?: boolean
  pixelRatio?: number
}

const AvatarPreview: React.FC<AvatarPreviewProps> = ({
  previewUrl,
  backgroundColor = AVATAR_PREVIEW_CONFIG.background.color,
  backgroundImageUrl = undefined,
  scale = AVATAR_PREVIEW_CONFIG.model.defaultScale,
  position = AVATAR_PREVIEW_CONFIG.model.defaultPosition,
  enableControls = false,
  pixelRatio = AVATAR_PREVIEW_CONFIG.renderer.pixelRatio,
}) => {
  const viewerRef = useRef<HTMLDivElement | null>(null)

  const sceneRef = useRef<THREE.Scene | null>(null)
  const cameraRef = useRef<
    THREE.OrthographicCamera | THREE.PerspectiveCamera | null
  >(null)
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null)
  const controlsRef = useRef<OrbitControls | null>(null)
  const backgroundMeshRef = useRef<THREE.Mesh | null>(null)
  const modelGroupRef = useRef<THREE.Group | null>(null)
  const modelRef = useRef<THREE.Object3D | null>(null)

  // loop & resize
  const rafIdRef = useRef<number | null>(null)
  const roRef = useRef<ResizeObserver | null>(null)
  const resizeDebounceTimerRef = useRef<number | null>(null)
  const resizeRafRef = useRef<number | null>(null)

  const ORTHO_VIEW_SIZE = AVATAR_PREVIEW_CONFIG.camera.orthoViewSize

  // ---------- helpers ----------
  const disposeBackgroundMesh = () => {
    const bg = backgroundMeshRef.current
    if (!bg) return
    if ((bg.material as any)?.map) {
      ;((bg.material as any).map as THREE.Texture).dispose()
    }
    bg.geometry.dispose()
    ;(bg.material as THREE.Material).dispose()
    sceneRef.current?.remove(bg)
    backgroundMeshRef.current = null
  }

  const buildBackground = () => {
    const scene = sceneRef.current!,
      camera = cameraRef.current!,
      renderer = rendererRef.current!
    if (!scene || !camera || !renderer) return

    const canvas = document.createElement('canvas')
    canvas.width = 1024
    canvas.height = 1024
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const drawGradient = () => {
      const g = ctx.createLinearGradient(0, 0, 0, canvas.height)
      g.addColorStop(1, '#ffffff')
      g.addColorStop(0.5, '#f8f8f8')
      g.addColorStop(0, '#f0f0f0')
      ctx.fillStyle = g
      ctx.fillRect(0, 0, canvas.width, canvas.height)
    }

    const makeTexture = () => {
      const tex = new THREE.CanvasTexture(canvas)
      tex.colorSpace = THREE.SRGBColorSpace
      tex.minFilter = THREE.LinearMipmapLinearFilter
      tex.magFilter = THREE.LinearFilter
      tex.generateMipmaps = true
      tex.anisotropy = Math.min(renderer.capabilities.getMaxAnisotropy(), 16)
      return tex
    }

    const computePlaneSize = () => {
      let w: number, h: number
      if ((camera as any).isOrthographicCamera) {
        const oc = camera as THREE.OrthographicCamera
        w = Math.abs(oc.right - oc.left)
        h = Math.abs(oc.top - oc.bottom)
      } else {
        const pc = camera as THREE.PerspectiveCamera
        const vFOV = THREE.MathUtils.degToRad(pc.fov)
        const dist = Math.abs(
          camera.position.z - AVATAR_PREVIEW_CONFIG.background.position[2],
        )
        h = 2 * Math.tan(vFOV / 2) * dist
        w = h * pc.aspect
      }
      return { w, h }
    }

    const createPlane = (texture: THREE.Texture) => {
      const { w, h } = computePlaneSize()
      disposeBackgroundMesh()
      const geo = new THREE.PlaneGeometry(w, h)
      const mat = new THREE.MeshBasicMaterial({ map: texture })
      const mesh = new THREE.Mesh(geo, mat)
      mesh.position.set(...AVATAR_PREVIEW_CONFIG.background.position)
      mesh.renderOrder = -1
      scene.add(mesh)
      backgroundMeshRef.current = mesh
    }

    const loadOrPaint = (onReady: (t: THREE.Texture) => void) => {
      if (backgroundImageUrl) {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
          const cw = canvas.width,
            ch = canvas.height
          const iw = img.width,
            ih = img.height
          const s = Math.max(cw / iw, ch / ih) // cover
          const sw = iw * s,
            sh = ih * s
          const dx = (cw - sw) / 2,
            dy = (ch - sh) / 2
          ctx.clearRect(0, 0, cw, ch)
          ctx.drawImage(img, dx, dy, sw, sh)
          onReady(makeTexture())
        }
        img.onerror = () => {
          drawGradient()
          onReady(makeTexture())
        }
        img.src = backgroundImageUrl
      } else {
        drawGradient()
        onReady(makeTexture())
      }
    }

    return new Promise<void>(resolve => {
      loadOrPaint(tex => {
        createPlane(tex)
        resolve()
      })
    })
  }

  const handleResizeNow = () => {
    const container = viewerRef.current
    const camera = cameraRef.current
    const renderer = rendererRef.current
    if (!container || !camera || !renderer) return

    const width = Math.max(1, container.clientWidth)
    const height = Math.max(1, container.clientHeight)
    const aspect = width / height

    if ((camera as any).isOrthographicCamera) {
      const halfH = ORTHO_VIEW_SIZE / 2
      const halfW = aspect * halfH
      const oc = camera as THREE.OrthographicCamera
      oc.left = -halfW
      oc.right = halfW
      oc.top = halfH
      oc.bottom = -halfH
    } else {
      ;(camera as THREE.PerspectiveCamera).aspect = aspect
    }
    camera.updateProjectionMatrix()

    renderer.setSize(width, height, false)
    renderer.setPixelRatio(
      Math.min(window.devicePixelRatio * (pixelRatio || 1), 3),
    )
    renderer.domElement.style.width = '100%'
    renderer.domElement.style.height = '100%'

    buildBackground()
  }

  const scheduleResize = () => {
    if (resizeDebounceTimerRef.current)
      window.clearTimeout(resizeDebounceTimerRef.current)
    resizeDebounceTimerRef.current = window.setTimeout(() => {
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current)
      resizeRafRef.current = requestAnimationFrame(handleResizeNow)
    }, 80) as unknown as number
  }

  const startLoop = () => {
    const renderer = rendererRef.current!
    const scene = sceneRef.current!
    const camera = cameraRef.current!
    const animate = () => {
      renderer.render(scene, camera)
      rafIdRef.current = requestAnimationFrame(animate)
    }
    animate()
  }

  const stopLoop = () => {
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current)
    rafIdRef.current = null
  }

  const disposeModelOnly = () => {
    if (!modelGroupRef.current) return
    modelGroupRef.current.children.forEach(child => {
      child.traverse(obj => {
        const mesh = obj as THREE.Mesh
        if ((mesh as any).isMesh) {
          mesh.geometry?.dispose()
          const m = mesh.material as THREE.Material | THREE.Material[]
          if (Array.isArray(m)) m.forEach(mm => mm.dispose())
          else m?.dispose()
        }
      })
    })
    modelGroupRef.current.clear()
    modelRef.current = null
  }

  const disposeAll = () => {
    stopLoop()
    controlsRef.current?.dispose()
    controlsRef.current = null

    disposeModelOnly()
    disposeBackgroundMesh()

    const renderer = rendererRef.current
    const container = viewerRef.current
    renderer?.dispose()
    if (container && renderer?.domElement.parentElement === container) {
      container.removeChild(renderer.domElement)
    }

    rendererRef.current = null
    sceneRef.current = null
    cameraRef.current = null
  }

  // ---------- init once ----------
  useEffect(() => {
    const container = viewerRef.current
    if (!container || !previewUrl) return

    container.style.overflow = 'hidden'
    container.style.display = 'block'

    const width = Math.max(1, container.clientWidth || 600)
    const height = Math.max(1, container.clientHeight || 320)
    const aspect = width / height
    const halfH = ORTHO_VIEW_SIZE / 2
    const halfW = aspect * halfH

    // scene
    const scene = new THREE.Scene()
    sceneRef.current = scene

    const camera = new THREE.OrthographicCamera(
      -halfW,
      halfW,
      halfH,
      -halfH,
      0.1,
      1000,
    )
    camera.position.set(...AVATAR_PREVIEW_CONFIG.camera.position)
    cameraRef.current = camera

    // renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true,
      powerPreference: 'high-performance',
      precision: 'highp',
    })
    renderer.setClearColor(backgroundColor, 1)
    renderer.setSize(width, height, false)
    renderer.setPixelRatio(
      Math.min(window.devicePixelRatio * (pixelRatio || 1), 3),
    )
    renderer.outputColorSpace = THREE.SRGBColorSpace
    renderer.shadowMap.enabled = true
    renderer.shadowMap.type = THREE.PCFSoftShadowMap
    renderer.toneMapping = THREE.ACESFilmicToneMapping
    renderer.toneMappingExposure = 1.5
    renderer.domElement.style.visibility = 'hidden'
    rendererRef.current = renderer

    container.innerHTML = ''
    container.appendChild(renderer.domElement)

    // controls
    const controls = new OrbitControls(camera, renderer.domElement)
    controls.enableRotate = enableControls
    controls.enableZoom = enableControls
    controls.enablePan = enableControls
    controlsRef.current = controls
    const initPipeline = async () => {
      await buildBackground()
      setupLights(scene)
      const modelGroup = new THREE.Group()
      modelGroup.position.set(...position)
      scene.add(modelGroup)
      modelGroupRef.current = modelGroup

      if (previewUrl) {
        await new Promise<void>(resolve => {
          const loader = new GLTFLoader()
          loader.load(
            previewUrl,
            gltf => {
              const model = gltf.scene
              modelRef.current = model

              const box = new THREE.Box3().setFromObject(model)
              const center = new THREE.Vector3()
              box.getCenter(center)
              model.position.x -= center.x
              model.position.z -= center.z
              model.position.y -= box.min.y

              model.scale.set(scale, scale, scale)
              model.rotation.set(0, 0, 0)
              modelGroup.add(model)

              model.traverse(obj => {
                const mesh = obj as THREE.Mesh
                if ((mesh as any).isMesh) {
                  mesh.frustumCulled = false
                  const applyTex = (m: any) => {
                    if (m?.map) {
                      m.map.generateMipmaps = true
                      m.map.minFilter = THREE.LinearMipmapLinearFilter
                      m.map.magFilter = THREE.LinearFilter
                      m.map.anisotropy = Math.min(
                        renderer.capabilities.getMaxAnisotropy(),
                        16,
                      )
                      m.map.wrapS = THREE.RepeatWrapping
                      m.map.wrapT = THREE.RepeatWrapping
                    }
                    ;['normalMap', 'roughnessMap', 'metalnessMap'].forEach(
                      k => {
                        if (m?.[k]) {
                          m[k].generateMipmaps = true
                          m[k].minFilter = THREE.LinearMipmapLinearFilter
                          m[k].magFilter = THREE.LinearFilter
                          m[k].anisotropy = Math.min(
                            renderer.capabilities.getMaxAnisotropy(),
                            16,
                          )
                        }
                      },
                    )
                  }
                  const mt = mesh.material as THREE.Material | THREE.Material[]
                  if (Array.isArray(mt)) mt.forEach(applyTex)
                  else applyTex(mt)
                }
              })

              resolve()
            },
            undefined,
            () => resolve(),
          )
        })
      }

      renderer.domElement.style.visibility = 'visible'
      startLoop()
    }

    initPipeline()

    const ro = new ResizeObserver(() => {
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current)
      resizeRafRef.current = requestAnimationFrame(scheduleResize)
    })
    ro.observe(container)
    roRef.current = ro

    return () => {
      ro.disconnect()
      roRef.current = null
      if (resizeDebounceTimerRef.current)
        window.clearTimeout(resizeDebounceTimerRef.current)
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current)
      disposeAll()
    }
  }, [
    /* init only when these truly change root context: */ backgroundColor,
    pixelRatio,
    enableControls,
    position?.[0],
    position?.[1],
    position?.[2],
  ])

  useEffect(() => {
    if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return
    ;(async () => {
      await buildBackground()
    })()
  }, [backgroundImageUrl])

  useEffect(() => {
    if (!previewUrl || !sceneRef.current || !rendererRef.current) return
    rendererRef.current!.domElement.style.visibility = 'hidden'

    disposeModelOnly()

    const loader = new GLTFLoader()
    const renderer = rendererRef.current!
    const modelGroup = modelGroupRef.current!

    loader.load(
      previewUrl,
      gltf => {
        disposeModelOnly()
        const model = gltf.scene
        modelRef.current = model

        const box = new THREE.Box3().setFromObject(model)
        const center = new THREE.Vector3()
        box.getCenter(center)
        model.position.x -= center.x
        model.position.z -= center.z
        model.position.y -= box.min.y

        model.scale.set(scale, scale, scale)
        model.rotation.set(0, 0, 0)
        modelGroup.add(model)

        model.traverse(obj => {
          const mesh = obj as THREE.Mesh
          if ((mesh as any).isMesh) {
            mesh.frustumCulled = false
            const applyTex = (m: any) => {
              if (m?.map) {
                m.map.generateMipmaps = true
                m.map.minFilter = THREE.LinearMipmapLinearFilter
                m.map.magFilter = THREE.LinearFilter
                m.map.anisotropy = Math.min(
                  renderer.capabilities.getMaxAnisotropy(),
                  16,
                )
                m.map.wrapS = THREE.RepeatWrapping
                m.map.wrapT = THREE.RepeatWrapping
              }
              ;['normalMap', 'roughnessMap', 'metalnessMap'].forEach(k => {
                if (m?.[k]) {
                  m[k].generateMipmaps = true
                  m[k].minFilter = THREE.LinearMipmapLinearFilter
                  m[k].magFilter = THREE.LinearFilter
                  m[k].anisotropy = Math.min(
                    renderer.capabilities.getMaxAnisotropy(),
                    16,
                  )
                }
              })
            }
            const mt = mesh.material as THREE.Material | THREE.Material[]
            if (Array.isArray(mt)) mt.forEach(applyTex)
            else applyTex(mt)
          }
        })

        renderer.domElement.style.visibility = 'visible'
      },
      undefined,
      () => {
        renderer.domElement.style.visibility = 'visible'
      },
    )
  }, [previewUrl, scale])

  return (
    <div
      ref={viewerRef}
      style={{
        width: '100%',
        height: '100%',
        background: '#fff',
      }}
    />
  )
}

export default AvatarPreview
